import React from 'react';

/**
 * ShareBadge — small "this is shared" indicator that appears next to
 * a board name or list title when the underlying doc has any of the
 * share-state fields populated (editors, viewers, or shareToken).
 *
 * Renders null when the doc isn't shared, so callers can drop it in
 * unconditionally without worrying about when it should appear.
 *
 * Click reveals "who it's shared with" by reopening the existing
 * ShareModal — owner-mode if the current user owns the doc (full
 * invite/remove UI), or viewOnly mode if they're a guest (read-only
 * participants list). The caller decides which mode to pass.
 *
 * Props:
 *   editors        Array of editor uids on the doc (or undefined).
 *   viewers        Array of viewer uids on the doc (or undefined).
 *   shareToken     Public-link token string (or null/undefined).
 *   onClick        Click handler. Receives the click event so the
 *                  caller can stopPropagation if the badge sits inside
 *                  another clickable surface (sidebar row, dropdown
 *                  entry). The badge itself stops propagation and
 *                  calls preventDefault before invoking onClick.
 *   title          Optional tooltip. Defaults to "this is shared —
 *                  click to see who".
 *   size           Pixel size of the icon glyph (default 11). Title
 *                  context governs sizing — sidebar rows want smaller,
 *                  tile headers slightly larger.
 */
export default function ShareBadge({
  editors,
  viewers,
  shareToken,
  // Walter 2026-05-12: board-level fallback. If the list itself has no
  // editors/viewers/shareToken but the BOARD it's on does, surface the
  // badge anyway — every tile on a shared board needs the cue so users
  // don't write a "private" note thinking it's just theirs. The tooltip
  // shifts to "this list is on a shared board" so the user understands
  // it's inherited rather than directly shared.
  boardEditors,
  boardViewers,
  onClick,
  title = 'this is shared — click to see who',
  size = 11,
  // Color tone. 'accent' (default) keeps the orange "this is shared"
  // signal that's loud enough to spot in a tile header or dropdown.
  // 'dim' grays it down for the new sidebar nav (Walter found a wall
  // of orange share-icons + bells too busy in the boards list).
  tone = 'accent',
}) {
  const editorsCount = Array.isArray(editors) ? editors.length : 0;
  const viewersCount = Array.isArray(viewers) ? viewers.length : 0;
  const boardEditorsCount = Array.isArray(boardEditors) ? boardEditors.length : 0;
  const boardViewersCount = Array.isArray(boardViewers) ? boardViewers.length : 0;
  const directlyShared = editorsCount > 0 || viewersCount > 0 || !!shareToken;
  const inheritedFromBoard = !directlyShared && (boardEditorsCount > 0 || boardViewersCount > 0);
  const isShared = directlyShared || inheritedFromBoard;
  if (!isShared) return null;
  const effectiveTitle = inheritedFromBoard
    ? 'this list is on a shared board — click to see who'
    : title;
  // Inherited-only badges use a slightly dimmer accent so a user can
  // still distinguish "this list was explicitly shared" (loud accent)
  // from "this list inherits from a shared board" (quiet accent). The
  // existing 'dim' tone option is preserved for the sidebar callers.
  const effectiveColor = tone === 'dim'
    ? 'var(--text-dimmer)'
    : (inheritedFromBoard ? 'var(--accent-dim)' : 'var(--accent)');

  const handleClick = (e) => {
    // Click bubbles into row/dropdown navigators by default; stop here
    // so the user lands in the modal instead of switching boards or
    // opening the list.
    e.stopPropagation();
    e.preventDefault();
    onClick?.(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={effectiveTitle}
      aria-label={effectiveTitle}
      style={{
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        padding: '2px',
        margin: 0,
        cursor: 'pointer',
        color: effectiveColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius)',
        lineHeight: 1,
        transition: 'color 0.12s, background 0.12s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--surface2)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* Two-people glyph — reads as "this is shared with someone".
          Drawn at 14×14 viewBox so the strokes scale cleanly with the
          `size` prop (which sets the rendered width/height). */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
      >
        {/* Front person */}
        <circle cx="5.2" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.1" />
        <path
          d="M1.5 12 C1.5 9.5 3 8 5.2 8 C7.4 8 8.9 9.5 8.9 12"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          fill="none"
        />
        {/* Back person */}
        <circle cx="9.6" cy="3.7" r="1.7" stroke="currentColor" strokeWidth="1.1" />
        <path
          d="M8.2 7.4 C9 7.1 10.2 7.1 11 7.4 C12 7.8 12.8 9 12.8 10.5"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </button>
  );
}
